import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { updateMe, uploadFile, fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AvatarUploader } from "@/components/AvatarUploader";
import { Upload, X, CheckCircle2, Lock, User, MapPin, Phone, Building2, CreditCard, ShieldCheck } from "lucide-react";

const ID_TYPES = [
  { value: "aadhaar", label: "Aadhaar Card" },
  { value: "pan", label: "PAN Card" },
  { value: "passport", label: "Passport" },
  { value: "drivers_license", label: "Driver's License" },
];

export default function ProfilePage() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    bio: "",
    mobile: "",
    address: "",
    bank: { account_holder: "", account_number: "", ifsc: "", bank_name: "" },
    identity: { id_type: "aadhar", id_number: "", doc_path: "" },
  });

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        bio: user.bio || "",
        mobile: user.mobile || "",
        address: user.address || "",
        bank: {
          account_holder: user.bank?.account_holder || "",
          account_number: user.bank?.account_number || "",
          ifsc: user.bank?.ifsc || "",
          bank_name: user.bank?.bank_name || "",
        },
        identity: {
          id_type: user.identity?.id_type === "aadhar" ? "aadhaar" : (user.identity?.id_type || "aadhaar"),
          id_number: user.identity?.id_number || "",
          doc_path: user.identity?.doc_path || "",
        },
      });
    }
  }, [user]);

  if (!user) return <div className="p-16 text-center">Loading…</div>;

  const set = (path, value) => setForm((f) => {
    const next = { ...f };
    const parts = path.split(".");
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) {
      cur[parts[i]] = { ...cur[parts[i]] };
      cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = value;
    return next;
  });

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Upload an image (JPG/PNG)"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8MB"); return; }
    setUploading(true);
    try {
      const r = await uploadFile(file);
      set("identity.doc_path", r.path);
      toast.success("ID uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await updateMe(form);
      await refresh?.();
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  // Completeness for UI feedback (mirrors backend)
  const b = form.bank; const idn = form.identity;
  const filled = {
    name: !!form.name.trim(),
    mobile: !!form.mobile.trim(),
    address: !!form.address.trim(),
    bank: !!(b.account_holder && b.account_number && b.ifsc && b.bank_name),
    identity: !!(idn.id_type && idn.id_number && idn.doc_path),
  };
  const doneCount = Object.values(filled).filter(Boolean).length;
  const totalCount = Object.keys(filled).length;
  const pct = Math.round((doneCount / totalCount) * 100);

  return (
    <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-10">
      <div className="overline text-primary mb-2 flex items-center gap-2">◆ SELLER PROFILE</div>
      <h1 className="text-4xl font-bold tracking-tight mb-2">Your identity & payout</h1>
      <p className="text-muted-foreground mb-8">Complete this so we can pay out your earnings. All info is private — only visible to admins.</p>

      {/* Progress */}
      <div data-testid="profile-progress" className={`border ${pct === 100 ? "border-primary" : "border-foreground/15"} p-5 mb-8`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {pct === 100 ? <CheckCircle2 className="w-5 h-5 text-primary"/> : <ShieldCheck className="w-5 h-5"/>}
            <div className="font-semibold">{pct === 100 ? "Profile complete — you can withdraw" : `Profile ${pct}% complete`}</div>
          </div>
          <div className="mono text-xs text-muted-foreground">{doneCount}/{totalCount} sections</div>
        </div>
        <div className="h-1 bg-foreground/10">
          <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }}/>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4 text-xs mono">
          {Object.entries(filled).map(([k, v]) => (
            <div key={k} className={`flex items-center gap-1 ${v ? "text-primary" : "text-muted-foreground"}`}>
              {v ? <CheckCircle2 className="w-3.5 h-3.5"/> : <span className="w-3.5 h-3.5 border border-current rounded-full inline-block"/>}
              <span className="uppercase">{k}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Personal */}
      <Section icon={<User className="w-4 h-4"/>} title="Personal information">
        <div className="mb-6"><AvatarUploader/></div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Full name" required>
            <Input data-testid="prof-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none border-foreground/20"/>
          </Field>
          <Field label="Email (from Google)" hint={<><Lock className="w-3 h-3 inline mr-1"/> non-editable</>}>
            <Input data-testid="prof-email" value={user.email} disabled readOnly className="rounded-none border-foreground/20 bg-muted/40 cursor-not-allowed"/>
          </Field>
          <Field label="Mobile number" required>
            <div className="flex">
              <span className="px-3 py-2 border border-foreground/20 border-r-0 bg-muted mono text-sm"><Phone className="w-3.5 h-3.5"/></span>
              <Input data-testid="prof-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="+91 98765 43210" className="rounded-none border-foreground/20"/>
            </div>
          </Field>
          <Field label="Short bio">
            <Input data-testid="prof-bio" value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="What do you do best?" className="rounded-none border-foreground/20"/>
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Full address" required icon={<MapPin className="w-3.5 h-3.5"/>}>
            <Textarea data-testid="prof-address" value={form.address} onChange={(e) => set("address", e.target.value)} rows={2} placeholder="Street, city, state, postal code, country" className="rounded-none border-foreground/20"/>
          </Field>
        </div>
      </Section>

      {/* Bank */}
      <Section icon={<Building2 className="w-4 h-4"/>} title="Bank account (for payouts)">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Account holder name" required>
            <Input data-testid="prof-bank-holder" value={form.bank.account_holder} onChange={(e) => set("bank.account_holder", e.target.value)} className="rounded-none border-foreground/20"/>
          </Field>
          <Field label="Bank name" required>
            <Input data-testid="prof-bank-name" value={form.bank.bank_name} onChange={(e) => set("bank.bank_name", e.target.value)} placeholder="e.g. HDFC Bank" className="rounded-none border-foreground/20"/>
          </Field>
          <Field label="Account number" required>
            <Input data-testid="prof-bank-account" value={form.bank.account_number} onChange={(e) => set("bank.account_number", e.target.value.replace(/\s/g, ""))} className="rounded-none border-foreground/20 mono"/>
          </Field>
          <Field label="IFSC / SWIFT" required>
            <Input data-testid="prof-bank-ifsc" value={form.bank.ifsc} onChange={(e) => set("bank.ifsc", e.target.value.toUpperCase())} className="rounded-none border-foreground/20 mono uppercase"/>
          </Field>
        </div>
        <div className="mt-4 border border-primary/40 bg-primary/5 p-3 text-xs mono uppercase text-primary flex items-center gap-2">
          <CreditCard className="w-4 h-4"/> A 20% platform fee applies to every withdrawal.
        </div>
      </Section>

      {/* Identity */}
      <Section icon={<ShieldCheck className="w-4 h-4"/>} title="Government ID">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="ID type" required>
            <select
              data-testid="prof-id-type"
              value={form.identity.id_type}
              onChange={(e) => set("identity.id_type", e.target.value)}
              className="w-full h-10 px-3 border border-foreground/20 rounded-none bg-background"
            >
              {ID_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="ID number" required>
            <Input data-testid="prof-id-number" value={form.identity.id_number} onChange={(e) => set("identity.id_number", e.target.value.toUpperCase())} className="rounded-none border-foreground/20 mono uppercase"/>
          </Field>
        </div>
        <div className="mt-4">
          <Field label="ID document image" required>
            {form.identity.doc_path ? (
              <div className="border border-foreground/20 p-3 flex items-start gap-4">
                <img src={fileUrl(form.identity.doc_path)} alt="ID" className="w-32 h-20 object-cover border border-foreground/10"/>
                <div className="flex-1 min-w-0">
                  <div className="mono text-xs mb-2 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-primary"/> Uploaded</div>
                  <button
                    data-testid="prof-id-remove"
                    onClick={() => set("identity.doc_path", "")}
                    className="mono text-xs text-destructive inline-flex items-center gap-1 hover:underline"
                  ><X className="w-3 h-3"/> Remove</button>
                </div>
              </div>
            ) : (
              <label className="inline-flex items-center gap-2 border border-dashed border-foreground/30 px-4 py-3 mono text-xs cursor-pointer hover:border-foreground w-full">
                <Upload className="w-3.5 h-3.5"/> {uploading ? "UPLOADING…" : "UPLOAD ID IMAGE (JPG/PNG · MAX 8MB)"}
                <input data-testid="prof-id-input" type="file" accept="image/*" hidden onChange={uploadDoc} disabled={uploading}/>
              </label>
            )}
          </Field>
        </div>
      </Section>

      <div className="flex items-center justify-between border-t border-foreground/10 pt-6">
        <Link to="/dashboard" className="mono text-xs text-muted-foreground hover:text-foreground">← Back to dashboard</Link>
        <button
          data-testid="prof-save-btn"
          onClick={save}
          disabled={saving}
          className="bg-primary text-primary-foreground px-8 py-3 mono text-xs hover:bg-primary/90 disabled:opacity-40"
        >{saving ? "SAVING…" : "SAVE PROFILE"}</button>
      </div>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <section className="mb-8 border border-foreground/15 p-6 bg-card">
      <div className="overline mb-5 flex items-center gap-2">{icon} ◆ {title}</div>
      {children}
    </section>
  );
}

function Field({ label, required, hint, children, icon }) {
  return (
    <div>
      <label className="overline block mb-2 flex items-center gap-1.5">
        {icon}◆ {label}{required && <span className="text-destructive">*</span>}
        {hint && <span className="opacity-60 normal-case text-[10px] ml-2">{hint}</span>}
      </label>
      {children}
    </div>
  );
}
